import {
    Alert,
    Badge,
    Button,
    Col,
    Collapse,
    Container,
    Form,
    Row,
    Spinner,
    Tab,
    Tabs,
} from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from 'react';

import SEO from '@/components/common/SEO';
import InfoTooltip from '@/components/common/InfoTooltip';
import { useAuth } from '@/shared/contexts/AuthContext';
import dataApi from '@/shared/services/dataApi';
import useChatPageEffect from '@/shared/hooks/useChatPageEffect';

import DataProductCard from './components/DataProductCard';
import DataSourceCard from './components/DataSourceCard';
import DiscoveryMap from './components/DiscoveryMap';
import OrganizationCard from './components/OrganizationCard';
import SmartPagination from './components/SmartPagination';
import './DataDiscovery.css';

// Plain-language definition of the pillar values in normalizeDataSource's
// PILLAR_LABELS.
const PILLAR_EXPLANATION =
    'Sustainability pillars are the three broad areas this site sorts data into: Environmental (nature and climate), Social (people and communities), and Economic (jobs and cost of living).';

const TABS = [
    { key: 'all', label: 'All' },
    { key: 'sources', label: 'Data Sources' },
    { key: 'products', label: 'Data Products' },
    { key: 'organizations', label: 'Organizations' },
];

const SORT_OPTIONS = {
    sources: [
        { value: 'name', label: 'Name' },
        { value: 'updated_at', label: 'Last Updated' },
        { value: 'organization', label: 'Organization' },
    ],
    products: [
        { value: 'name', label: 'Name' },
        { value: 'updated_at', label: 'Last Updated' },
        { value: 'organization', label: 'Organization' },
    ],
    organizations: [
        { value: 'org_name', label: 'Name' },
        { value: 'updated_at', label: 'Last Updated' },
        { value: 'org_type', label: 'Type' },
    ],
};

const STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'error', label: 'Error' },
    { value: 'pending', label: 'Pending' },
    { value: 'deprecated', label: 'Deprecated' },
    { value: 'new', label: 'New' },
];

export default function DataDiscovery() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { isAdmin } = useAuth();

    // Read URL state
    const tab = searchParams.get('tab') || 'all';
    const q = searchParams.get('q') || '';
    const spotlight = searchParams.get('spotlight') || '';
    const pillar = searchParams.get('pillar') || '';
    const origin = searchParams.get('origin') || '';
    const status = searchParams.get('status') || '';
    const sort = searchParams.get('sort') || '';
    const order = searchParams.get('order') || 'asc';
    const page = parseInt(searchParams.get('page') || '1', 10);

    // Local state
    const [searchInput, setSearchInput] = useState(q);
    const [filtersOpen, setFiltersOpen] = useState(
        !!(spotlight || pillar || origin || status)
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Data
    const [sources, setSources] = useState([]);
    const [products, setProducts] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [sourcePagination, setSourcePagination] = useState(null);
    const [productPagination, setProductPagination] = useState(null);
    const [orgPagination, setOrgPagination] = useState(null);

    // Map data (sources + products with geometry for the coverage map)
    const [mapSources, setMapSources] = useState([]);
    const [mapProducts, setMapProducts] = useState([]);

    // Card-to-map hover highlighting
    const [highlightedItemId, setHighlightedItemId] = useState(null);
    const [highlightedItemType, setHighlightedItemType] = useState(null);

    // Spatial filter — filter results by map viewport
    const [spatialFilterActive, setSpatialFilterActive] = useState(false);
    const [mapBounds, setMapBounds] = useState(null);

    const boundsTimerRef = useRef(null);
    const handleBoundsChange = useCallback((bounds) => {
        // Debounce to avoid refetching on every tiny pan
        if (boundsTimerRef.current) clearTimeout(boundsTimerRef.current);
        boundsTimerRef.current = setTimeout(() => {
            setMapBounds(bounds);
        }, 400);
    }, []);

    const handleSourceHover = useCallback((id) => {
        setHighlightedItemId(id);
        setHighlightedItemType(id != null ? 'source' : null);
    }, []);
    const handleProductHover = useCallback((id) => {
        setHighlightedItemId(id);
        setHighlightedItemType(id != null ? 'product' : null);
    }, []);

    // URL update helper — replaces search params without navigation
    const updateParams = useCallback(
        (updates) => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                Object.entries(updates).forEach(([k, v]) => {
                    if (v) {
                        next.set(k, v);
                    } else {
                        next.delete(k);
                    }
                });
                return next;
            });
        },
        [setSearchParams]
    );

    // Fetch map data (sources + products with geometry) once on mount
    useEffect(() => {
        let cancelled = false;
        async function fetchMapData() {
            try {
                const [srcRes, prodRes] = await Promise.all([
                    dataApi.getSources({
                        limit: 100,
                        include_geometry: 'true',
                    }),
                    dataApi.getProducts({
                        limit: 100,
                        include_geometry: 'true',
                    }),
                ]);
                if (!cancelled) {
                    setMapSources(srcRes.data_sources || []);
                    setMapProducts(prodRes.data_products || []);
                }
            } catch {
                // Map data is non-critical; fail silently
            }
        }
        fetchMapData();
        return () => {
            cancelled = true;
        };
    }, []);

    // Stable bbox string for use as effect dependency (avoids object reference issues)
    const bboxString = useMemo(
        () =>
            spatialFilterActive && mapBounds
                ? `${mapBounds.west},${mapBounds.south},${mapBounds.east},${mapBounds.north}`
                : '',
        [spatialFilterActive, mapBounds]
    );

    // Fetch data when URL params change
    useEffect(() => {
        let cancelled = false;

        async function fetchData() {
            setLoading(true);
            setError(null);

            // Admin status filter: empty string → API skips status filter (show all),
            // specific value → filters by that status. Non-admin → undefined (API defaults to active).
            const statusParam = isAdmin ? status : undefined;

            const baseParams = {
                q: q || undefined,
                spotlight: spotlight || undefined,
                pillar: pillar || undefined,
                status: statusParam,
                bbox: bboxString || undefined,
                page,
                limit: 12,
            };

            try {
                // Build fetch promises in parallel when on "All" tab (Task #123)
                const promises = [];

                if (tab === 'all' || tab === 'sources') {
                    const sourceParams = {
                        ...baseParams,
                        origin: origin || undefined,
                        sort: sort || undefined,
                        order: sort ? order : undefined,
                    };
                    if (tab === 'all') sourceParams.limit = 6;

                    promises.push(
                        dataApi.getSources(sourceParams).then((res) => {
                            if (!cancelled) {
                                setSources(res.data_sources || []);
                                setSourcePagination(res.pagination || null);
                            }
                        })
                    );
                }

                if (tab === 'all' || tab === 'products') {
                    const productParams = {
                        ...baseParams,
                        sort: sort || undefined,
                        order: sort ? order : undefined,
                    };
                    if (tab === 'all') productParams.limit = 6;

                    promises.push(
                        dataApi.getProducts(productParams).then((res) => {
                            if (!cancelled) {
                                setProducts(res.data_products || []);
                                setProductPagination(res.pagination || null);
                            }
                        })
                    );
                }

                if (tab === 'all' || tab === 'organizations') {
                    const orgParams = {
                        q: q || undefined,
                        page: tab === 'organizations' ? page : 1,
                        limit: tab === 'all' ? 6 : 12,
                        sort: sort || undefined,
                        order: sort ? order : undefined,
                    };

                    promises.push(
                        dataApi.getOrganizations(orgParams).then((res) => {
                            if (!cancelled) {
                                setOrganizations(res.organizations || []);
                                setOrgPagination(res.pagination || null);
                            }
                        })
                    );
                }

                await Promise.all(promises);
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        fetchData();
        return () => {
            cancelled = true;
        };
    }, [
        tab,
        q,
        spotlight,
        pillar,
        origin,
        status,
        sort,
        order,
        page,
        isAdmin,
        bboxString,
    ]);

    // Handlers
    const handleSearch = (e) => {
        e.preventDefault();
        updateParams({ q: searchInput, page: '' });
    };

    const handleTabChange = (newTab) => {
        updateParams({
            tab: newTab === 'all' ? '' : newTab,
            page: '',
            sort: '',
            order: '',
        });
    };

    const handleFilterChange = (key, value) => {
        updateParams({ [key]: value, page: '' });
    };

    const clearFilters = () => {
        updateParams({
            spotlight: '',
            pillar: '',
            origin: '',
            status: '',
            sort: '',
            order: '',
            q: '',
            page: '',
        });
        setSearchInput('');
        setSpatialFilterActive(false);
        setMapBounds(null);
    };

    const handlePageChange = (newPage) => {
        updateParams({ page: newPage > 1 ? String(newPage) : '' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSortChange = (value) => {
        if (!value) {
            updateParams({ sort: '', order: '' });
        } else {
            updateParams({ sort: value, order });
        }
    };

    const toggleOrder = () => {
        updateParams({ order: order === 'asc' ? 'desc' : 'asc' });
    };

    // Determine active pagination based on tab
    const activePagination =
        tab === 'sources'
            ? sourcePagination
            : tab === 'products'
              ? productPagination
              : tab === 'organizations'
                ? orgPagination
                : null;

    const totalResults =
        tab === 'all'
            ? (sourcePagination?.total || 0) +
              (productPagination?.total || 0) +
              (orgPagination?.total || 0)
            : activePagination?.total || 0;

    const hasActiveFilters = !!(
        spotlight ||
        pillar ||
        origin ||
        status ||
        q ||
        spatialFilterActive
    );

    // Publish rich page context for the floating chat widget.
    // Includes current filters, search query, and names of visible items so
    // Bili knows exactly what the user is currently looking at.
    const discoveryContextDeps = useMemo(
        () => [
            tab,
            q,
            spotlight,
            pillar,
            origin,
            status,
            bboxString,
            sources.length,
            products.length,
            organizations.length,
        ],
        [
            tab,
            q,
            spotlight,
            pillar,
            origin,
            status,
            bboxString,
            sources.length,
            products.length,
            organizations.length,
        ]
    );
    useChatPageEffect(
        {
            page: 'data-discovery',
            section: 'data-discovery',
            tab,
            search: q || null,
            spotlight: spotlight || null,
            pillar: pillar || null,
            origin: origin || null,
            spatialFilter: bboxString || null,
            visibleSources: sources.map((s) => s.name),
            visibleProducts: products.map((p) => p.name),
            visibleOrganizations: organizations.map((o) => o.org_name),
            totalSources: sourcePagination?.total || 0,
            totalProducts: productPagination?.total || 0,
            totalOrganizations: orgPagination?.total || 0,
        },
        discoveryContextDeps
    );

    return (
        <>
            <SEO
                title="Data Discovery"
                description="Explore sustainability data sources, products, and organizations."
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Data Discovery', path: '/data' },
                ]}
            />
            <section id="data-discovery" className="page-container bg-light">
                <Container className="py-4">
                    {/* Header */}
                    <Row className="mb-4">
                        <Col>
                            <h1 className="text-primary fw-bold mb-2">
                                Data Discovery
                            </h1>
                            <p className="text-body-secondary mb-0">
                                Browse environmental data sources, products, and
                                organizations powering Colorado sustainability
                                research.
                            </p>
                        </Col>
                    </Row>

                    {/* Search */}
                    <Row className="mb-3">
                        <Col lg={8} xl={6}>
                            <Form onSubmit={handleSearch}>
                                <Form.Group className="d-flex gap-2">
                                    <Form.Control
                                        type="search"
                                        placeholder="Search data sources, products, and organizations..."
                                        value={searchInput}
                                        onChange={(e) =>
                                            setSearchInput(e.target.value)
                                        }
                                        aria-label="Search data"
                                    />
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={
                                            searchInput.length > 0 &&
                                            searchInput.length < 2
                                        }
                                    >
                                        Search
                                    </Button>
                                </Form.Group>
                            </Form>
                        </Col>
                    </Row>

                    {/* Tabs */}
                    <Tabs
                        activeKey={tab}
                        onSelect={handleTabChange}
                        className="mb-3"
                    >
                        {TABS.map((t) => (
                            <Tab key={t.key} eventKey={t.key} title={t.label} />
                        ))}
                    </Tabs>

                    {/* Filters + Sort row */}
                    <Row className="mb-3 align-items-center">
                        <Col>
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <Button
                                    variant="outline-dark"
                                    size="sm"
                                    onClick={() => setFiltersOpen(!filtersOpen)}
                                    aria-expanded={filtersOpen}
                                    aria-controls="advanced-filters"
                                >
                                    {filtersOpen
                                        ? 'Hide Filters'
                                        : 'Advanced Filters'}
                                </Button>
                                {hasActiveFilters && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="text-danger p-0"
                                        onClick={clearFilters}
                                    >
                                        Clear all
                                    </Button>
                                )}
                                {/* Active filter badges */}
                                {spotlight && (
                                    <Badge bg="primary">{spotlight}</Badge>
                                )}
                                {pillar && <Badge bg="success">{pillar}</Badge>}
                                {origin && <Badge bg="info">{origin}</Badge>}
                                {status && (
                                    <Badge bg="warning" text="dark">
                                        {status}
                                    </Badge>
                                )}
                                {spatialFilterActive && (
                                    <Badge bg="success">Map area</Badge>
                                )}
                            </div>
                        </Col>
                        <Col
                            xs="auto"
                            className="d-flex align-items-center gap-2"
                        >
                            <small className="text-muted">
                                {totalResults} result
                                {totalResults !== 1 ? 's' : ''}
                            </small>
                            {tab !== 'all' && (
                                <>
                                    <Form.Select
                                        size="sm"
                                        value={sort}
                                        onChange={(e) =>
                                            handleSortChange(e.target.value)
                                        }
                                        aria-label="Sort by"
                                        className="w-auto"
                                    >
                                        <option value="">Sort by...</option>
                                        {(
                                            SORT_OPTIONS[tab] ||
                                            SORT_OPTIONS.sources
                                        ).map((opt) => (
                                            <option
                                                key={opt.value}
                                                value={opt.value}
                                            >
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    {sort && (
                                        <Button
                                            variant="outline-dark"
                                            size="sm"
                                            onClick={toggleOrder}
                                            aria-label={`Sort ${order === 'asc' ? 'descending' : 'ascending'}`}
                                        >
                                            {order === 'asc'
                                                ? '\u2191'
                                                : '\u2193'}
                                        </Button>
                                    )}
                                </>
                            )}
                        </Col>
                    </Row>

                    {/* Collapsible filters */}
                    <Collapse in={filtersOpen}>
                        <div id="advanced-filters" className="mb-3">
                            <Row className="g-2">
                                <Col xs={6} md={3}>
                                    <Form.Select
                                        size="sm"
                                        value={spotlight}
                                        onChange={(e) =>
                                            handleFilterChange(
                                                'spotlight',
                                                e.target.value
                                            )
                                        }
                                        aria-label="Filter by spotlight"
                                    >
                                        <option value="">All Spotlights</option>
                                        <option value="water">Water</option>
                                        <option value="wildfires">
                                            Wildfires
                                        </option>
                                        <option value="data-centers-front-range">
                                            Data Centers
                                        </option>
                                    </Form.Select>
                                </Col>
                                <Col
                                    xs={6}
                                    md={3}
                                    className="d-flex align-items-center gap-1"
                                >
                                    <Form.Select
                                        size="sm"
                                        className="flex-grow-1"
                                        value={pillar}
                                        onChange={(e) =>
                                            handleFilterChange(
                                                'pillar',
                                                e.target.value
                                            )
                                        }
                                        aria-label="Filter by pillar"
                                    >
                                        <option value="">All Pillars</option>
                                        <option value="environmental">
                                            Environmental
                                        </option>
                                        <option value="economic">
                                            Economic
                                        </option>
                                        <option value="social">Social</option>
                                    </Form.Select>
                                    <InfoTooltip text={PILLAR_EXPLANATION} />
                                </Col>
                                {(tab === 'all' || tab === 'sources') && (
                                    <Col xs={6} md={3}>
                                        <Form.Select
                                            size="sm"
                                            value={origin}
                                            onChange={(e) =>
                                                handleFilterChange(
                                                    'origin',
                                                    e.target.value
                                                )
                                            }
                                            aria-label="Filter by data origin"
                                        >
                                            <option value="">
                                                All Origins
                                            </option>
                                            <option value="Federal">
                                                Federal
                                            </option>
                                            <option value="State">State</option>
                                            <option value="Private">
                                                Private
                                            </option>
                                        </Form.Select>
                                    </Col>
                                )}
                                {isAdmin && (
                                    <Col xs={6} md={3}>
                                        <Form.Select
                                            size="sm"
                                            value={status}
                                            onChange={(e) =>
                                                handleFilterChange(
                                                    'status',
                                                    e.target.value
                                                )
                                            }
                                            aria-label="Filter by status"
                                        >
                                            {STATUS_OPTIONS.map((opt) => (
                                                <option
                                                    key={opt.value}
                                                    value={opt.value}
                                                >
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                )}
                            </Row>
                        </div>
                    </Collapse>

                    {/* Error */}
                    {error && (
                        <Alert variant="danger" className="mb-3">
                            <Alert.Heading>Something went wrong</Alert.Heading>
                            <p className="mb-0">{error}</p>
                        </Alert>
                    )}

                    {/* Loading */}
                    {loading && (
                        <div className="d-flex justify-content-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    )}

                    {/* Results */}
                    {!loading && !error && (
                        <>
                            {/* All tab — grouped sections */}
                            {tab === 'all' && (
                                <>
                                    {(mapSources.length > 0 ||
                                        mapProducts.length > 0) && (
                                        <div className="mb-3">
                                            <DiscoveryMap
                                                sources={mapSources}
                                                products={mapProducts}
                                                highlightedItemId={
                                                    highlightedItemId
                                                }
                                                highlightedItemType={
                                                    highlightedItemType
                                                }
                                                spatialFilterActive={
                                                    spatialFilterActive
                                                }
                                                onBoundsChange={
                                                    handleBoundsChange
                                                }
                                            />
                                            <div className="d-flex justify-content-end mt-1">
                                                <Button
                                                    variant={
                                                        spatialFilterActive
                                                            ? 'success'
                                                            : 'outline-info'
                                                    }
                                                    size="sm"
                                                    onClick={() => {
                                                        setSpatialFilterActive(
                                                            (active) => {
                                                                // Turning the
                                                                // filter off
                                                                // clears the
                                                                // bounds.
                                                                // Deriving this
                                                                // from the
                                                                // updater's
                                                                // current value
                                                                // (not the
                                                                // render-
                                                                // captured
                                                                // state) keeps
                                                                // it correct
                                                                // when clicks
                                                                // batch in one
                                                                // render.
                                                                if (active) {
                                                                    setMapBounds(
                                                                        null
                                                                    );
                                                                }
                                                                return !active;
                                                            }
                                                        );
                                                    }}
                                                    aria-pressed={
                                                        spatialFilterActive
                                                    }
                                                    aria-label="Filter results by map area"
                                                >
                                                    {spatialFilterActive
                                                        ? 'Spatial filter on'
                                                        : 'Filter by map area'}
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                    {sources.length > 0 && (
                                        <section className="mb-4">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h2 className="h5 text-primary mb-0">
                                                    Data Sources
                                                    <Badge
                                                        bg="light"
                                                        text="dark"
                                                        className="ms-2 fw-normal"
                                                    >
                                                        {sourcePagination?.total ||
                                                            sources.length}
                                                    </Badge>
                                                </h2>
                                                {sourcePagination?.total >
                                                    6 && (
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() =>
                                                            handleTabChange(
                                                                'sources'
                                                            )
                                                        }
                                                    >
                                                        View all{' '}
                                                        {sourcePagination.total}{' '}
                                                        &rarr;
                                                    </Button>
                                                )}
                                            </div>
                                            <Row className="g-3">
                                                {sources.map((s) => (
                                                    <Col
                                                        key={s.id}
                                                        md={6}
                                                        lg={4}
                                                    >
                                                        <DataSourceCard
                                                            source={s}
                                                            onHover={
                                                                handleSourceHover
                                                            }
                                                        />
                                                    </Col>
                                                ))}
                                            </Row>
                                        </section>
                                    )}

                                    {products.length > 0 && (
                                        <section className="mb-4">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h2 className="h5 text-primary mb-0">
                                                    Data Products
                                                    <Badge
                                                        bg="light"
                                                        text="dark"
                                                        className="ms-2 fw-normal"
                                                    >
                                                        {productPagination?.total ||
                                                            products.length}
                                                    </Badge>
                                                </h2>
                                                {productPagination?.total >
                                                    6 && (
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() =>
                                                            handleTabChange(
                                                                'products'
                                                            )
                                                        }
                                                    >
                                                        View all{' '}
                                                        {
                                                            productPagination.total
                                                        }{' '}
                                                        &rarr;
                                                    </Button>
                                                )}
                                            </div>
                                            <Row className="g-3">
                                                {products.map((p) => (
                                                    <Col
                                                        key={p.id}
                                                        md={6}
                                                        lg={4}
                                                    >
                                                        <DataProductCard
                                                            product={p}
                                                            onHover={
                                                                handleProductHover
                                                            }
                                                        />
                                                    </Col>
                                                ))}
                                            </Row>
                                        </section>
                                    )}

                                    {organizations.length > 0 && (
                                        <section className="mb-4">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h2 className="h5 text-primary mb-0">
                                                    Organizations
                                                    <Badge
                                                        bg="light"
                                                        text="dark"
                                                        className="ms-2 fw-normal"
                                                    >
                                                        {orgPagination?.total ||
                                                            organizations.length}
                                                    </Badge>
                                                </h2>
                                                {orgPagination?.total > 6 && (
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() =>
                                                            handleTabChange(
                                                                'organizations'
                                                            )
                                                        }
                                                    >
                                                        View all{' '}
                                                        {orgPagination.total}{' '}
                                                        &rarr;
                                                    </Button>
                                                )}
                                            </div>
                                            <Row className="g-3">
                                                {organizations.map((o) => (
                                                    <Col
                                                        key={o.org_id}
                                                        md={6}
                                                        lg={4}
                                                    >
                                                        <OrganizationCard
                                                            org={o}
                                                        />
                                                    </Col>
                                                ))}
                                            </Row>
                                        </section>
                                    )}

                                    {sources.length === 0 &&
                                        products.length === 0 &&
                                        organizations.length === 0 && (
                                            <div className="text-center py-5 text-muted">
                                                <p className="fs-5 mb-1">
                                                    No results found
                                                </p>
                                                <p>
                                                    Try adjusting your search or
                                                    filters.
                                                </p>
                                            </div>
                                        )}
                                </>
                            )}

                            {/* Sources tab */}
                            {tab === 'sources' && (
                                <>
                                    <Row className="g-3">
                                        {sources.map((s) => (
                                            <Col key={s.id} md={6} lg={4}>
                                                <DataSourceCard source={s} />
                                            </Col>
                                        ))}
                                    </Row>
                                    {sources.length === 0 && (
                                        <div className="text-center py-5 text-muted">
                                            <p className="fs-5 mb-1">
                                                No data sources found
                                            </p>
                                            <p>
                                                Try adjusting your search or
                                                filters.
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Products tab */}
                            {tab === 'products' && (
                                <>
                                    <Row className="g-3">
                                        {products.map((p) => (
                                            <Col key={p.id} md={6} lg={4}>
                                                <DataProductCard product={p} />
                                            </Col>
                                        ))}
                                    </Row>
                                    {products.length === 0 && (
                                        <div className="text-center py-5 text-muted">
                                            <p className="fs-5 mb-1">
                                                No data products found
                                            </p>
                                            <p>
                                                Try adjusting your search or
                                                filters.
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Organizations tab */}
                            {tab === 'organizations' && (
                                <>
                                    <Row className="g-3">
                                        {organizations.map((o) => (
                                            <Col key={o.org_id} md={6} lg={4}>
                                                <OrganizationCard org={o} />
                                            </Col>
                                        ))}
                                    </Row>
                                    {organizations.length === 0 && (
                                        <div className="text-center py-5 text-muted">
                                            <p className="fs-5 mb-1">
                                                No organizations found
                                            </p>
                                            <p>
                                                Try adjusting your search or
                                                filters.
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Pagination (not shown on "all" tab) */}
                            {tab !== 'all' && activePagination && (
                                <div className="mt-4">
                                    <SmartPagination
                                        page={activePagination.page}
                                        pages={activePagination.pages}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Container>
            </section>
        </>
    );
}
